import { useState } from 'react';

import { Button as MuiButton, Divider, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  BOX_SOURCES,
  draftFor,
} from '@/james-playground/aa-family-claim/claim/drafts';
import { type Value } from '@/james-playground/aa-family-claim/claim/facts';
import { applies } from '@/james-playground/aa-family-claim/claim/engine';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { NARRATIVE } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  Field,
  fieldLabel,
  Source,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  known,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

type Box = { text: string; status: 'draft' | 'approved' | 'editing' };

const isBox = (id: string) => id in BOX_SOURCES || id === 'variability';
const isTellUsMore = (id: string) =>
  Object.values(BOX_SOURCES).some((s) => s.tellUsMore === id);

/**
 * The words on the form (spec 10). Each "tell us more" box that applies shows
 * the draft built from the family's own words, to approve or edit. Approving
 * puts the words in the box and ticks its "tell us more" Yes. A box nothing
 * the family said covers is left out, and its "tell us more" is No. Q46 and
 * Q63 work the same way: drafted where there is a draft, typed where there
 * is not.
 */
const NarrativeCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const qid = useQids();
  const boxes = ask.entries.filter(isBox).filter((box) => {
    const asks = box === 'variability' ? box : BOX_SOURCES[box].tellUsMore;
    return applies(entry(asks), facts) === true;
  });
  const [state, setState] = useState<Record<string, Box>>(() =>
    Object.fromEntries(
      boxes.flatMap((box): [string, Box][] => {
        const existing = str(known(facts, box));
        if (existing) return [[box, { text: existing, status: 'approved' }]];
        const draft = draftFor(box, facts);
        return draft ? [[box, { text: draft.text, status: 'draft' }]] : [];
      }),
    ),
  );
  const typed = ask.entries.filter((id) => !isBox(id) && !isTellUsMore(id));
  const [free, setFree] = useState<Record<string, string>>(() =>
    Object.fromEntries(typed.map((id) => [id, str(known(facts, id))])),
  );

  const drafted = boxes.filter((box) => state[box]);
  const pending = drafted.filter((box) => state[box].status !== 'approved');
  const patch = (box: string, next: Partial<Box>) =>
    setState((s) => ({ ...s, [box]: { ...s[box], ...next } }));
  const approve = (box: string) =>
    patch(box, { status: 'approved', text: state[box].text.trim() });

  const submit = () => {
    const values: Record<string, Value> = {};
    for (const box of boxes) {
      const approved =
        state[box]?.status === 'approved' && state[box].text.trim();
      if (approved) values[box] = state[box].text.trim();
      if (box !== 'variability')
        values[BOX_SOURCES[box].tellUsMore] = approved ? 'Yes' : 'No';
    }
    for (const id of typed) values[id] = free[id].trim();
    const n = drafted.length;
    const added = typed.some((id) => free[id].trim());
    onSubmit(
      values,
      n
        ? NARRATIVE.reply(n)
        : added
          ? typed.map((id) => free[id].trim()).join(' ')
          : NARRATIVE.nothing,
    );
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={pending.length === 0}
      onContinue={submit}
      onSkip={onSkip}
    >
      {pending.length > 1 && (
        <MuiButton
          variant='outlined'
          onClick={() => pending.forEach(approve)}
          sx={{ alignSelf: 'flex-start' }}
        >
          {NARRATIVE.approveAll}
        </MuiButton>
      )}
      {drafted.length > 0 && (
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          {drafted.map((box) => {
            const b = state[box];
            const name = NARRATIVE.box[box] ? say(NARRATIVE.box[box], v) : box;
            return (
              <Stack
                key={box}
                spacing={1.25}
                role='group'
                aria-labelledby={qid(box)}
              >
                <Typography id={qid(box)} variant='h6'>
                  {name}
                </Typography>
                <Source>{NARRATIVE.drafted}</Source>
                {b.status === 'editing' ? (
                  <Field
                    multiline
                    minRows={3}
                    label={NARRATIVE.editLabel(name.toLowerCase())}
                    value={b.text}
                    onChange={(e) => patch(box, { text: e.target.value })}
                  />
                ) : (
                  <Typography variant='body1' sx={{ whiteSpace: 'pre-line' }}>
                    {b.text}
                  </Typography>
                )}
                <Stack
                  sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
                  direction='row'
                >
                  {b.status === 'editing' ? (
                    <MuiButton
                      variant='contained'
                      disabled={!b.text.trim()}
                      onClick={() => approve(box)}
                    >
                      {NARRATIVE.done}
                    </MuiButton>
                  ) : (
                    <>
                      {b.status === 'draft' && (
                        <MuiButton
                          variant='contained'
                          onClick={() => approve(box)}
                        >
                          {NARRATIVE.approve}
                        </MuiButton>
                      )}
                      <MuiButton
                        variant='outlined'
                        onClick={() => patch(box, { status: 'editing' })}
                      >
                        {NARRATIVE.edit}
                      </MuiButton>
                    </>
                  )}
                  {b.status === 'approved' && (
                    <Stack
                      sx={{ alignItems: 'center', color: 'primary.main' }}
                      direction='row'
                      spacing={0.5}
                    >
                      <Icon icon='check' fontSize={14} />
                      <Typography variant='body2'>
                        {NARRATIVE.approved}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
      {drafted.length === 0 && boxes.length > 0 && (
        <Typography variant='body2'>{NARRATIVE.none}</Typography>
      )}
      {typed.map((id) => (
        <Field
          key={id}
          multiline
          minRows={3}
          label={
            id === 'extraInformation' ? NARRATIVE.extra : fieldLabel(id, v)
          }
          helperText={NARRATIVE.extraHint}
          value={free[id]}
          onChange={(e) => setFree((f) => ({ ...f, [id]: e.target.value }))}
        />
      ))}
    </CardShell>
  );
};

NarrativeCard.displayName = 'NarrativeCard';
export default NarrativeCard;
